import { z } from "zod";

import { ROLES } from "@/types/prompt-blueprint";

/**
 * Content block schemas - flexible validation allowing extra fields.
 * We validate that required fields exist, but don't reject unknown fields.
 */
const textContentSchema = z
  .object({
    type: z.literal("text"),
    text: z.string(),
  })
  .passthrough();

const thinkingContentSchema = z
  .object({
    type: z.literal("thinking"),
    thinking: z.string(),
  })
  .passthrough();

const imageContentSchema = z
  .object({
    type: z.literal("image_url"),
    image_url: z
      .object({
        url: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

const mediaContentSchema = z
  .object({
    type: z.literal("media"),
    media: z
      .object({
        type: z.string(),
        url: z.string(),
        title: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

const mediaVariableSchema = z
  .object({
    type: z.literal("media_variable"),
    name: z.string(),
  })
  .passthrough();

const codeContentSchema = z
  .object({
    type: z.literal("code"),
    code: z.string(),
  })
  .passthrough();

/**
 * Union of all valid content block types.
 * Falls back to allowing any object with a type field for forward compatibility.
 */
const contentBlockSchema = z.union([
  textContentSchema,
  thinkingContentSchema,
  imageContentSchema,
  mediaContentSchema,
  mediaVariableSchema,
  codeContentSchema,
  // Fallback: any object with a type field (forward compatibility)
  z.object({ type: z.string() }).passthrough(),
]);

/**
 * Tool call schema (for assistant messages).
 */
const toolCallSchema = z
  .object({
    id: z.string(),
    type: z.literal("function"),
    function: z.object({
      name: z.string(),
      arguments: z.string(),
    }),
  })
  .passthrough();

/**
 * Function call schema (legacy format).
 */
const functionCallSchema = z
  .object({
    name: z.string(),
    arguments: z.string(),
  })
  .passthrough();

/**
 * Base message schema - fields common to all messages.
 */
const baseMessageSchema = z
  .object({
    id: z.string(),
    role: z.string(),
    content: z.array(contentBlockSchema),
  })
  .passthrough();

/**
 * Role-specific message schemas with additional required fields.
 */
const systemMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.SYSTEM),
});

const userMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.USER),
});

const developerMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.DEVELOPER),
});

const assistantMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.ASSISTANT),
  tool_calls: z.array(toolCallSchema).optional(),
  function_call: functionCallSchema.optional(),
});

const functionMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.FUNCTION),
  name: z.string(),
});

const toolMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.TOOL),
  tool_call_id: z.string(),
});

const placeholderMessageSchema = baseMessageSchema.extend({
  role: z.literal(ROLES.PLACEHOLDER),
  name: z.string().optional(),
});

/**
 * Map of role to its specific schema.
 */
export const messageSchemaByRole: Record<string, z.ZodTypeAny> = {
  [ROLES.SYSTEM]: systemMessageSchema,
  [ROLES.USER]: userMessageSchema,
  [ROLES.ASSISTANT]: assistantMessageSchema,
  [ROLES.FUNCTION]: functionMessageSchema,
  [ROLES.TOOL]: toolMessageSchema,
  [ROLES.PLACEHOLDER]: placeholderMessageSchema,
  [ROLES.DEVELOPER]: developerMessageSchema,
};

/**
 * Get the appropriate schema for a message based on its role.
 * Falls back to base schema for unknown roles.
 */
export const getSchemaForRole = (role: string): z.ZodTypeAny =>
  messageSchemaByRole[role] ?? baseMessageSchema;
